<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useInfiniteScroll } from '@vueuse/core'
import { useHistoryPage } from '../composables/useHistoryPage'
import type { HistoryCommonConfig, HistoryDeleteModalTheme } from '../configs/types'
import type { HistoryBook, HistoryPagedState } from '../types'
import { novelmelloHistoryTheme as ui } from '../configs/novelmello'
import HistoryEditOverlay from './HistoryEditOverlay.vue'
import { resources } from '@/config/brands'
import { t } from '@/i18n/utils'

const props = defineProps<{
  config: HistoryCommonConfig
  deleteModalTheme: HistoryDeleteModalTheme
}>()

const coverPlaceholder = resources.bookCoverPlaceholder
const infiniteRef = ref<HTMLElement>()

// 逻辑 100% 复用 useHistoryPage，本组件只负责皮肤
const {
  from,
  activeTab,
  editMode,
  selectedIds,
  deleteConfirmVisible,
  library,
  readHistory,
  isAllSelected,
  showTabbar,
  loadCurrent,
  switchTab,
  enterEdit,
  exitEdit,
  toggleSelectAll,
  openDeleteConfirm,
  closeDeleteConfirm,
  confirmDelete,
  onItemClick,
  onCoverError,
  toBookRowView,
} = useHistoryPage(props.config, coverPlaceholder)

useInfiniteScroll(infiniteRef, async () => {
  await loadCurrent()
}, { distance: 50 })

/** 当前 tab 对应的分页状态（两个 tab 共用同一段模板） */
const panel = computed<HistoryPagedState<HistoryBook>>(
  () => (activeTab.value === 'library' ? library : readHistory),
)

/** 行视图模型，避免模板里重复调用 toBookRowView（纯函数，无副作用） */
const rows = computed(() => panel.value.items.map(item => ({
  item,
  view: toBookRowView(item, activeTab.value),
})))

/**
 * 底部留白：
 * - 非编辑态且展示 Tabbar：浮动胶囊 Tabbar（高 56 + 距底 12，由 02-home 实现）
 * - 编辑态：底部操作条
 * - 其余（带 from 进入）：常规留白
 */
const rootPaddingClass = computed(() => {
  if (showTabbar.value)
    return 'pb-92px'
  return editMode.value ? 'pb-80px' : 'pb-12px'
})
</script>

<template>
  <div
    class="nm-history h-100dvh flex flex-col overflow-hidden bg-n-bg-global"
    :class="rootPaddingClass"
  >
    <!-- 带 from 时保留返回导航（逻辑同 default） -->
    <div v-if="from" class="flex-shrink-0">
      <BasicNavbar />
      <div class="h-5" />
    </div>

    <!-- ① 顶部：tab 行 -->
    <div v-if="!editMode" class="flex-shrink-0 flex items-center justify-between px-20px py-16px">
      <div class="flex items-end space-x-24px">
        <div class="cursor-pointer" @click="switchTab('library')">
          <p
            class="nm-display text-20px lh-24px font-700"
            :class="activeTab === 'library' ? 'text-n-primary' : 'text-n-text-3'"
          >
            {{ t('LibraryTab') }}
          </p>
          <div
            class="mt-4px mx-a h-4px w-22px rounded-4px"
            :style="activeTab === 'library' ? { background: ui.gradient } : undefined"
          />
        </div>

        <div class="cursor-pointer" @click="switchTab('history')">
          <p
            class="nm-display text-20px lh-24px font-700"
            :class="activeTab === 'history' ? 'text-n-primary' : 'text-n-text-3'"
          >
            {{ t('ViewedTab') }}
          </p>
          <div
            class="mt-4px mx-a h-4px w-22px rounded-4px"
            :style="activeTab === 'history' ? { background: ui.gradient } : undefined"
          />
        </div>
      </div>

      <div class="cursor-pointer text-n-text-2" @click="enterEdit">
        <i-lucide-square-pen class="w-22px h-22px pointer-events-none" />
      </div>
    </div>

    <!-- ① 顶部：编辑态标题栏 -->
    <div v-else class="flex-shrink-0 relative flex items-center px-20px py-16px">
      <div class="cursor-pointer text-n-text-1" @click="exitEdit">
        <i-lucide-x class="w-22px h-22px pointer-events-none" />
      </div>
      <p class="nm-display absolute left-1/2 -translate-x-1/2 text-19px lh-24px font-700 text-n-text-1">
        {{ t('LibraryEdit') }}
      </p>
    </div>

    <!-- ② 白卡：列表 / 空态 -->
    <div
      ref="infiniteRef"
      class="nm-card flex-1 overflow-auto mx-16px rounded-20px bg-n-bg-card"
      :style="{ boxShadow: ui.cardShadow }"
    >
      <!-- 首屏 loading -->
      <div
        v-if="panel.fetching && panel.page === 1 && panel.items.length === 0"
        class="py-90px flex justify-center"
      >
        <BasicSpinner />
      </div>

      <!-- 空态 -->
      <div
        v-else-if="panel.items.length === 0"
        class="flex flex-col items-center justify-center px-20px py-90px"
      >
        <i-lucide-library-big class="w-72px h-72px" :style="{ color: ui.emptyIcon }" />
        <p class="mt-20px text-14px lh-20px text-n-text-3">
          {{ config.texts.noData }}
        </p>
        <router-link
          :to="{ name: 'home' }"
          replace
          class="mt-24px inline-block rounded-full text-14px font-700 lh-18px"
          :style="{
            color: ui.purple,
            border: `1.5px solid ${ui.purple}`,
            padding: '10px 26px',
          }"
        >
          {{ config.texts.goToNovel }}
        </router-link>
      </div>

      <!-- 列表（:key 带 activeTab，切 tab 强制重建，避免行复用串台） -->
      <div v-else :key="`${activeTab}-list`" class="p-16px">
        <div
          v-for="(row, index) in rows"
          :key="row.item.contentId"
          class="flex items-start"
          :class="index === 0 ? '' : 'mt-16px'"
          @click="onItemClick(row.item)"
        >
          <div class="relative flex-shrink-0">
            <img
              class="w-64px h-86px rounded-10px object-cover"
              :src="row.view.coverUrl || coverPlaceholder"
              alt=""
              @error="onCoverError"
            >
            <!-- 选择圈：由现有 selectedIds 驱动，未改任何多选逻辑 -->
            <div
              v-if="editMode"
              class="absolute right-4px bottom-4px w-20px h-20px rounded-full flex items-center justify-center"
              :style="selectedIds.has(row.item.contentId)
                ? { background: ui.gradient, border: '1.5px solid #FFFFFF' }
                : { background: 'rgba(255, 255, 255, 0.6)', border: '1.5px solid #FFFFFF' }"
            >
              <i-lucide-check
                v-if="selectedIds.has(row.item.contentId)"
                class="w-12px h-12px text-white"
              />
            </div>
          </div>

          <div class="ml-12px flex-1 min-w-0 pt-4px">
            <p class="text-15px lh-20px font-800 text-n-text-1 truncate">
              {{ row.view.title }}
            </p>
            <p
              v-for="line in row.view.metaLines"
              :key="line"
              class="mt-8px text-12px lh-16px text-n-text-3 truncate"
            >
              {{ line }}
            </p>
          </div>
        </div>

        <!-- 列表尾 -->
        <div v-if="panel.ending" class="pt-24px pb-4px text-center text-13px lh-18px text-n-text-3">
          {{ config.texts.noMore }}
        </div>
        <div
          v-else-if="panel.fetching"
          class="pt-20px pb-4px flex items-center justify-center space-x-4 text-13px text-n-text-3"
        >
          <div class="animate-spin">
            <i-icon-park-outline-loading />
          </div>
          <p>{{ config.texts.loading }}</p>
        </div>
      </div>
    </div>

    <!-- ③ 编辑态底部操作条（内层用 bg-n-bg-pure，交给品牌 SCSS 的 .fixed-bottom 统一着色） -->
    <BasicFixedBottom v-if="editMode" :style="{ boxShadow: ui.barShadow }">
      <div class="w-full flex items-center justify-between bg-n-bg-pure px-24px py-14px">
        <div class="flex items-center cursor-pointer text-n-text-1" @click="toggleSelectAll">
          <i-lucide-circle-check
            class="w-20px h-20px pointer-events-none"
            :class="isAllSelected ? 'text-n-primary' : 'text-n-text-3'"
          />
          <span class="ml-8px text-14px font-700 lh-18px whitespace-nowrap">
            {{ t('EditSelect') }}
          </span>
        </div>

        <div
          class="flex items-center cursor-pointer"
          :class="selectedIds.size === 0 ? '' : 'text-n-primary'"
          :style="selectedIds.size === 0 ? { color: ui.mutedPurple } : undefined"
          @click="openDeleteConfirm"
        >
          <i-lucide-trash-2 class="w-18px h-18px pointer-events-none" />
          <span class="ml-6px text-14px font-700 lh-18px whitespace-nowrap">
            {{ t('EditDelete') }} ({{ selectedIds.size }})
          </span>
        </div>
      </div>
    </BasicFixedBottom>

    <!-- ④ 删除二次确认弹窗：复用既有组件（edit-mode=false 时它只渲染 portal 弹窗，不渲染底部条） -->
    <HistoryEditOverlay
      :edit-mode="false"
      :is-all-selected="isAllSelected"
      :selected-count="selectedIds.size"
      :delete-confirm-visible="deleteConfirmVisible"
      :delete-modal-theme="deleteModalTheme"
      @toggle-all="toggleSelectAll"
      @request-delete="openDeleteConfirm"
      @close-delete="closeDeleteConfirm"
      @confirm-delete="confirmDelete"
    />

    <!-- ⑤ 底部浮动胶囊 Tabbar（样式由 02-home 计划统一改造，此处仅按现有逻辑挂载） -->
    <BasicTabbar v-if="showTabbar" tab="history" />
  </div>
</template>

<style scoped>
.nm-display {
  font-family: "Baloo 2", "Nunito Sans", -apple-system, BlinkMacSystemFont, sans-serif;
}

/* 白卡内部滚动条隐藏，保持设计稿干净观感 */
.nm-card {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.nm-card::-webkit-scrollbar {
  display: none;
}
</style>
